import './_reset-password.style.scss';

import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, notification, Spin } from 'antd';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import colors from '../../../foundation/assets/colors';
import LogoDark from '../../../foundation/assets/svgs/LogoDark';
import Password from '../../../foundation/components/password/Password';
import { useAppDispatch } from '../../../store/hooks';
import { ResetPasswordValidationSchema } from '../profile/profile_validation';
import {
  getPasswordResetInfo,
  getPasswordRules,
  resetPassword,
} from '../redux/async_thunks';

type KeyStatus = 'loading' | 'valid' | 'invalid' | 'conflict';

type ResetUser = {
  username?: string;
  email?: string;
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const key = searchParams.get('key');
  const [keyStatus, setKeyStatus] = useState<KeyStatus>('loading');
  const [keyError, setKeyError] = useState<string>('');
  const [resetUser, setResetUser] = useState<ResetUser | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordRules, setPasswordRules] = useState<string[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(getPasswordRules())
      .unwrap()
      .then((rules: string[]) => setPasswordRules(rules))
      .catch(() => {
        /* silently hide the box on failure */
      })
      .finally(() => setRulesLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (!key) {
      setKeyStatus('invalid');
      return;
    }

    dispatch(getPasswordResetInfo({ key }))
      .unwrap()
      .then((data: any) => {
        setResetUser(data?.data?.user ?? null);
        setKeyStatus('valid');
      })
      .catch((error: any) => {
        // Display allauth error message as-is, distinguish 409 (conflict) from other errors
        const allauthErrors: Array<{ message: string }> = error?.errors ?? [];
        setKeyError(
          allauthErrors.map((e) => e.message).join(' ') ||
            'Invalid reset link.',
        );
        setKeyStatus(error?.httpStatus === 409 ? 'conflict' : 'invalid');
      });
  }, [dispatch, key]);

  const formik = useFormik({
    initialValues: {
      new_password: '',
      confirm_password: '',
    },
    validationSchema: ResetPasswordValidationSchema,
    onSubmit: async (values, { setFieldError }) => {
      if (!key) {
        return;
      }

      setIsSubmitting(true);
      try {
        await dispatch(
          resetPassword({ key, password: values.new_password }),
        ).unwrap();
        // 200 — password reset successful
        setIsSuccess(true);
      } catch (error: any) {
        // 401 — password was reset successfully, but user is not auto-logged in
        // (ACCOUNT_LOGIN_ON_PASSWORD_RESET=False for HIPAA/BSI TR-03107 compliance)
        if (error?.httpStatus === 401) {
          setIsSuccess(true);
        } else {
          // Display allauth errors exactly as they come from the backend
          const allauthErrors: Array<{
            param?: string;
            message: string;
          }> = error?.errors ?? [];
          for (const err of allauthErrors) {
            if (err.param === 'password') {
              setFieldError('new_password', err.message);
            }
          }
          if (allauthErrors.length === 0) {
            notification.error({
              message: 'Password reset failed',
              description: error?.message ?? 'Failed to reset password.',
            });
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const goToLogin = () => navigate('/?login=1');

  if (keyStatus === 'loading') {
    return (
      <div className="l-reset-password__page">
        <Spin size="large" tip="Verifying your reset link..." />
      </div>
    );
  }

  if (keyStatus === 'invalid' || keyStatus === 'conflict') {
    return (
      <div className="l-reset-password__page">
        <div className="l-reset-password__card">
          <div className="l-reset-password__logo">
            <LogoDark />
          </div>
          <Alert
            type={keyStatus === 'conflict' ? 'warning' : 'error'}
            showIcon
            message={keyStatus === 'conflict' ? 'Conflict' : 'Invalid Link'}
            description={keyError}
            className="l-reset-password__alert"
          />
          <Button
            type="primary"
            onClick={goToLogin}
            className="l-reset-password__submit-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="l-reset-password__page">
        <div className="l-reset-password__card">
          <div className="l-reset-password__logo">
            <LogoDark />
          </div>
          <Alert
            type="success"
            showIcon
            message="Password Reset Successful"
            description="Your password has been reset successfully. Please log in with your new password."
            className="l-reset-password__alert"
          />
          <Button
            type="primary"
            onClick={goToLogin}
            className="l-reset-password__submit-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="l-reset-password__page">
      <div className="l-reset-password__card">
        <div className="l-reset-password__header">
          <div className="l-reset-password__logo">
            <LogoDark />
          </div>
          <div className="l-reset-password__title">
            <FontAwesomeIcon icon={faLock} color={colors.primary} />
            Reset Password
          </div>
        </div>

        {resetUser && (
          <div className="l-reset-password__account-chip">
            <span className="l-reset-password__account-avatar">
              {(resetUser.username || resetUser.email || '?')[0].toUpperCase()}
            </span>
            <span className="l-reset-password__account-name">
              {resetUser.username || resetUser.email}
            </span>
          </div>
        )}

        <div className="l-reset-password__form">
          <form onSubmit={formik.handleSubmit}>
            <div className="l-reset-password__field-wrapper">
              <label htmlFor="new_password">New password</label>
              <Password
                id="new_password"
                name="new_password"
                placeholder="Enter new password"
                value={formik.values.new_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                error={
                  formik.touched.new_password
                    ? formik.errors.new_password
                    : undefined
                }
              />
            </div>

            {rulesLoading ? (
              <div className="l-reset-password__rules-loading">
                <Spin size="small" />
              </div>
            ) : passwordRules.length > 0 ? (
              <Alert
                type="info"
                showIcon
                className="l-reset-password__rules-alert"
                message={
                  <ul className="l-reset-password__rules-list">
                    {passwordRules.map((rule, i) => (
                      <li key={i}>{rule}</li>
                    ))}
                  </ul>
                }
              />
            ) : null}

            <div className="l-reset-password__field-wrapper">
              <label htmlFor="confirm_password">Confirm new password</label>
              <Password
                id="confirm_password"
                name="confirm_password"
                placeholder="Repeat new password"
                value={formik.values.confirm_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                error={
                  formik.touched.confirm_password
                    ? formik.errors.confirm_password
                    : undefined
                }
              />
            </div>

            <div className="l-reset-password__actions">
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="l-reset-password__submit-btn"
              >
                Reset Password
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
